import React, { useMemo } from 'react'
import {
  PinkTag,
  PlanBoxUpToVisits,
  PlanBoxUpToWebsites,
} from 'Features/pricing/components/PlanBox/PlanBox.styled'

import { Trans, useTranslation } from 'react-i18next'
import { PlansJson } from 'Types/Plan'
import {
  Button,
  StyledText,
  theme,
  Tooltip,
  useResponsive,
} from '@clickcease/cc-client-components-lib'
import { PlanTypes } from 'Types/PlansTypes'
import { PlanBoxPrice } from './PlanBoxPrice'
import { CurrenciesTypes } from 'Types/Currencies'
import { Plan } from 'Features/pricing/types/Plan'
import { PRODUCT_TYPES, ProductTypes } from 'Types/ProductTypes'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'

const triggerStyle = {
  width: theme.space['6'].value,
  style: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.colors.gray.value,
  },
}

type CePlansProps = {
  numOfVisits: number
  enterpriseOrMaxYearlyVisits?: boolean
  planName: PlanTypes
  plansJson: PlansJson
  plansDb: Array<Plan>
  currency: CurrenciesTypes
  product: ProductTypes
  hidePrice?: boolean
  bundle?: boolean
  disableButton?: boolean
  crossSell?: boolean
  reactivation?: boolean
  handleClick?: () => void
}

export const CePlans = ({
  planName,
  numOfVisits,
  plansJson,
  plansDb,
  currency,
  product,
  hidePrice,
  bundle,
  crossSell,
  handleClick,
  enterpriseOrMaxYearlyVisits,
  disableButton,
  reactivation,
}: CePlansProps) => {
  const { t } = useTranslation('pricing')
  const { isLaptop, isMobile } = useResponsive()
  const pricingEndOfTrial = usePricingEndOfTrial()
  const buttonText = useMemo(() => {
    if (reactivation) {
      return t('reactivate')
    } else if (pricingEndOfTrial) {
      return t('continue')
    } else {
      return enterpriseOrMaxYearlyVisits ? t('lets_talk') : t('start_free_trial')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingEndOfTrial, enterpriseOrMaxYearlyVisits, reactivation])

  return (
    <>
      {isMobile && bundle && planName === 'Advanced' && (
        <PinkTag css={{ marginTop: '$12' }}>{t('best_for_agencies')}</PinkTag>
      )}
      {!hidePrice && (
        <PlanBoxPrice
          numOfVisits={numOfVisits}
          planName={planName}
          plansJson={plansJson}
          plansDb={plansDb}
          currency={currency}
          product={product}
        />
      )}
      {!bundle && !crossSell && (
        <Button
          css={{
            width: isLaptop ? '85%' : '226px',
            maxWidth: isLaptop ? '85%' : '226px',
            height: isLaptop ? '30px' : '42px',
            maxHeight: '31px',
            marginTop: isLaptop ? '10px' : '14px',
          }}
          text={buttonText}
          onClick={handleClick}
          disabled={disableButton}
          loading={disableButton}
        />
      )}
      <PlanBoxUpToWebsites>
        <Trans
          ns="pricing"
          i18nKey="paid_marketing.up_to_websites"
          values={{ num: plansJson[planName].protected_websites }}
          components={{ bold: <StyledText variant={'baseBold'} /> }}
        />
      </PlanBoxUpToWebsites>
      <PlanBoxUpToVisits>
        {numOfVisits > plansJson.max_yearly_visits ? (
          <Trans
            ns="pricing"
            i18nKey="paid_marketing.more_than_million_visits"
            values={{ num: plansJson.max_yearly_visits / 1000 }}
            components={{ bold: <StyledText variant={'baseBold'} /> }}
          />
        ) : (
          <Trans
            ns="pricing"
            i18nKey="paid_marketing.up_to_visits"
            values={{ num: (numOfVisits * 1000).toLocaleString() }}
            components={{ bold: <StyledText variant={'baseBold'} /> }}
          />
        )}
        {!isMobile && (
          <Tooltip
            trigerStyle={triggerStyle}
            tooltiptContent={t(
              product === PRODUCT_TYPES.PAID_MARKETING
                ? 'paid_marketing.tooltips.yearly_visits'
                : 'bot_mitigation.total_traffic'
            )}
          />
        )}
      </PlanBoxUpToVisits>
    </>
  )
}
